/**
 * Job status odometer — three circular dials showing this client's live job
 * pipeline (work in process / awaiting client / completed in last 90d).
 * Appears on the portal home when the `job_status_odometer` add-on is on.
 * Backed by GET /api/job-status.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Gauge, Cog, Hourglass, CheckCircle2 } from "lucide-react";

import { api } from "@/lib/api";

// Colours per bucket — kept muted so the tile blends with the portal palette
// but each dial is instantly distinguishable at a glance.
const DIALS = [
    { key: "work_in_process", icon: Cog,          stroke: "#0E3B2E", track: "#E8EEEB" },
    { key: "awaiting_client", icon: Hourglass,    stroke: "#B45309", track: "#FCEFDD" },
    { key: "completed_recent", icon: CheckCircle2, stroke: "#3F6212", track: "#E9EFDD" },
];

// A dial's "full tank" — used only to normalise the arc length. If a client
// has more than this many jobs in one bucket the dial pins to full but the
// count still reads the real number underneath.
const DIAL_MAX = 10;

function Dial({ count, label, hint, titles, icon: Icon, stroke, track }) {
    // Circle geometry: r=42, circumference ≈ 263.9
    const r = 42;
    const c = 2 * Math.PI * r;
    const capped = Math.min(count, DIAL_MAX);
    const dashOffset = c - (capped / DIAL_MAX) * c;
    return (
        <div className="flex flex-col items-center text-center min-w-0" data-testid={`odometer-dial-${label.toLowerCase().replaceAll(" ", "-")}`}>
            <div className="relative">
                <svg width="120" height="120" viewBox="0 0 100 100" className="rotate-[-90deg]">
                    <circle cx="50" cy="50" r={r} stroke={track} strokeWidth="10" fill="none" />
                    <circle
                        cx="50" cy="50" r={r}
                        stroke={stroke} strokeWidth="10" fill="none"
                        strokeLinecap="round"
                        strokeDasharray={c}
                        strokeDashoffset={dashOffset}
                        style={{ transition: "stroke-dashoffset 700ms ease-out" }}
                    />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                    <div className="flex flex-col items-center">
                        <Icon className="h-4 w-4 mb-0.5" style={{ color: stroke }} />
                        <div className="font-display text-2xl leading-none tabular-nums text-[#111]" data-testid={`odometer-count-${label.toLowerCase().replaceAll(" ", "-")}`}>{count}</div>
                    </div>
                </div>
            </div>
            <div className="mt-2 text-xs uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <div className="mt-1 text-[11px] text-[#5E636E] max-w-[180px] leading-snug">{hint}</div>
            {titles?.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-[#111] max-w-[180px]" data-testid={`odometer-titles-${label.toLowerCase().replaceAll(" ", "-")}`}>
                    {titles.slice(0, 2).map((t) => (
                        <li key={t} className="truncate" title={t}>• {t}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function JobStatusOdometer() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/job-status")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    const total = useMemo(() => {
        if (!data?.buckets) return 0;
        return Object.values(data.buckets).reduce((s, b) => s + (b.count || 0), 0);
    }, [data]);

    if (!loaded) {
        return (
            <div className="co-card co-card-pad text-sm text-[#5E636E]" data-testid="job-status-odometer-loading">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading job status…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="co-card co-card-pad" data-testid="job-status-odometer">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                        <Gauge className="h-3.5 w-3.5" /> Your jobs right now
                    </div>
                    <div className="mt-1 font-display text-lg font-medium text-[#111]">
                        {total === 0 ? "All clear" : `${total} job${total === 1 ? "" : "s"} on the go`}
                    </div>
                    <div className="mt-0.5 text-xs text-[#5E636E]">
                        A quick snapshot of what your accountant is working on, waiting for, or just finished.
                    </div>
                </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-4">
                {DIALS.map((d) => {
                    const b = data.buckets?.[d.key];
                    if (!b) return null;
                    return (
                        <Dial
                            key={d.key}
                            count={b.count || 0}
                            label={b.label}
                            hint={b.hint}
                            titles={b.titles}
                            icon={d.icon}
                            stroke={d.stroke}
                            track={d.track}
                        />
                    );
                })}
            </div>
        </div>
    );
}
